'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Wand2, Wrench, FolderOpen, ChevronLeft, ChevronRight, Crown, Bell, LogOut, User, ImageIcon, Library, Sparkles, X, CheckCircle, CreditCard, Save, AlertCircle, Briefcase, Image, Brain, Home, BookOpen, FileCode, Video, Mic, Share2 } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import LanguageSelector from '@/components/LanguageSelector';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { toast } from 'sonner';
import { VISUAL_STUDIO_ENABLED } from '@/lib/visualStudioConfig';
import { resolveUserDisplayIdentity } from '@/lib/userDisplayIdentity';
import { hasActivePaidPlan, planDisplayLabel } from '@/lib/planDisplayLabel';
import { getAiToolsVisibleCount } from '@/lib/aiToolsCatalog';

interface AppSidebarProps {
  collapsed: boolean;
  onCollapse: (v: boolean) => void;
}

// ─── DreamDNA Sparkle ─────────────────────────────────────────────────────────

function DreamSparkle() {
  const [visible, setVisible] = useState(false);
  const [reduced, setReduced] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);

    if (mq.matches) return; // static display, no animation

    const schedule = () => {
      const delay = 6000 + Math.random() * 2000; // 6–8 s
      timerRef.current = setTimeout(() => {
        setVisible(true);
        setTimeout(() => {
          setVisible(false);
          schedule();
        }, 1200);
      }, delay);
    };

    schedule();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <>
      <style>{`
        @keyframes dreamSparkle {
          0%   { opacity: 0; transform: scale(0.6); }
          25%  { opacity: 0.35; transform: scale(0.9); }
          45%  { opacity: 0.55; transform: scale(1); }
          65%  { opacity: 0.45; transform: scale(0.95); }
          100% { opacity: 0; transform: scale(0.7); }
        }
        .dream-sparkle-animate {
          animation: dreamSparkle 1.2s ease-in-out forwards;
        }
      `}</style>
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: -4,
          left: -4,
          width: 5,
          height: 5,
          pointerEvents: 'none',
          opacity: reduced ? 0.35 : 0,
          zIndex: 1,
        }}
        className={visible && !reduced ? 'dream-sparkle-animate' : ''}
      >
        <svg
          width="5"
          height="5"
          viewBox="0 0 11 11"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id="sparkleGradSidebar" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#c4b5fd" />
              <stop offset="50%" stopColor="#f9a8d4" />
              <stop offset="100%" stopColor="#fcd34d" />
            </linearGradient>
          </defs>
          <path
            d="M5.5 0 L6.1 4.4 L10.5 5.5 L6.1 6.6 L5.5 11 L4.9 6.6 L0.5 5.5 L4.9 4.4 Z"
            fill="url(#sparkleGradSidebar)"
          />
        </svg>
      </div>
    </>
  );
}

// ─── Ambient Logo Glow (fires once on load) ───────────────────────────────────

function AmbientLogoGlow() {
  const [active, setActive] = useState(false);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    if (!mq.matches) {
      // Small delay so it fires after first paint
      const t = setTimeout(() => setActive(true), 200);
      return () => clearTimeout(t);
    }
  }, []);

  if (reduced) return null;

  return (
    <>
      <style>{`
        @keyframes ambientLogoFade {
          0%   { opacity: 0; }
          45%  { opacity: 0.2; }
          100% { opacity: 0; }
        }
        .ambient-logo-fade {
          animation: ambientLogoFade 1.8s ease-out forwards;
        }
      `}</style>
      <div
        aria-hidden="true"
        className={active ? 'ambient-logo-fade' : ''}
        style={{
          position: 'absolute',
          inset: -2,
          borderRadius: '50%',
          background: 'radial-gradient(ellipse at center, rgba(167,139,250,0.06) 0%, rgba(249,168,212,0.02) 50%, transparent 68%)',
          pointerEvents: 'none',
          opacity: 0,
          zIndex: 0,
        }}
      />
    </>
  );
}

// ─── Notification Types ───────────────────────────────────────────────────────

interface Notification {
  id: string;
  type: 'success' | 'credit' | 'save' | 'billing' | 'info';
  title: string;
  message: string;
  time: string;
  read: boolean;
}

const NOTIFICATION_ICONS: Record<string, React.ReactNode> = {
  success: <CheckCircle size={14} className="text-green-400" />,
  credit: <CreditCard size={14} className="text-blue-400" />,
  save: <Save size={14} className="text-purple-400" />,
  billing: <AlertCircle size={14} className="text-yellow-400" />,
  info: <Bell size={14} className="text-muted-foreground" />,
};

export default function AppSidebar({ collapsed, onCollapse }: AppSidebarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { t } = useLanguage();
  const { user, signOut, loading: authLoading } = useAuth();
  const { subscription } = useSubscription();
  const [notifOpen, setNotifOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [notifications] = useState<Notification[]>([]);

  const planLabel = planDisplayLabel({
    plan: subscription?.plan,
    stripeSubscriptionId: subscription?.stripeSubscriptionId,
    status: subscription?.status,
  });
  const isPaidActive = hasActivePaidPlan({
    plan: subscription?.plan,
    stripeSubscriptionId: subscription?.stripeSubscriptionId,
    status: subscription?.status,
  });
  const identity =
    signingOut || authLoading || !user ? null : resolveUserDisplayIdentity(user);

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
      toast.success(t?.nav?.signOut || 'Signed out successfully');
    } catch {
      setSigningOut(false);
      toast.error(t?.errors?.generic || 'Failed to sign out');
    }
  };

  const markAllRead = () => {};

  // ─── Simplified Nav Groups ────────────────────────────────────────────────
  const navGroups = [
    {
      label: t?.nav?.sidebarMain || 'MAIN',
      items: [
        { label: t?.nav?.welcomeHub || 'Welcome Hub', href: '/welcome', icon: Home, badge: null, tourId: 'welcome-hub' },
        { label: t?.nav?.dreamDashboard || 'Dream Dashboard™', href: '/dream-dashboard', icon: Sparkles, badge: 'New', tourId: 'dream-dashboard' },
        { label: t?.nav?.dreamBuilder || 'Dream Builder™', href: '/dream-workspace', icon: Wand2, badge: 'Hot', tourId: 'dream-builder' },
        { label: t?.nav?.myProjects || t?.myProjects?.title || 'My Projects', href: '/my-projects', icon: FolderOpen, badge: null, tourId: 'my-projects' },
      ],
    },
    {
      label: t?.nav?.sidebarCreate || 'CREATE',
      items: [
        { label: t?.nav?.businessVault || 'Knowledge Vault™', href: '/business-knowledge-vault', icon: Brain, badge: 'New', tourId: 'knowledge-vault' },
        { label: t?.homePage?.businessBuilder || t?.nav?.businessBuilder || 'Business Builder', href: '/business-builder', icon: Briefcase, badge: null, tourId: 'business-builder' },
        ...(VISUAL_STUDIO_ENABLED
          ? [{ label: t?.nav?.sidebarVisualStudio || 'Visual Studio', href: '/image-studio', icon: Image, badge: null, tourId: 'visual-studio' }]
          : [{ label: t?.nav?.imageStudio || 'Image Studio', href: '/image-studio', icon: Image, badge: null, tourId: 'image-studio' }]),
        { label: t?.nav?.videoStudio || 'Video Studio', href: '/video-studio', icon: Video, badge: null, tourId: 'video-studio' },
        { label: t?.nav?.svgStudio || 'SVG Studio', href: '/svg-studio', icon: FileCode, badge: null, tourId: 'svg-studio' },
        { label: t?.nav?.voiceStudio || 'Voice Studio', href: '/voice-studio', icon: Mic, badge: null, tourId: 'voice-studio' },
        { label: t?.nav?.socialMediaStudio || 'Social Post Studio', href: '/social-media-studio', icon: Share2, badge: null, tourId: 'social-media-studio' },
        {
          label: t?.nav?.aiTools || 'AI Tools',
          href: '/ai-tools-page',
          icon: Wrench,
          badge: String(getAiToolsVisibleCount()),
          tourId: 'ai-tools',
        },
      ],
    },
    {
      label: t?.nav?.sidebarLibrary || 'LIBRARY',
      items: [
        { label: t?.nav?.myLibrary || 'My Library', href: '/my-library', icon: Library, badge: null, tourId: undefined },
        { label: t?.nav?.sidebarDreamGallery || 'Dream Gallery™', href: '/dream-workspace', icon: ImageIcon, badge: null, tourId: undefined },
        { label: t?.nav?.resources || 'Resources', href: '/resources', icon: BookOpen, badge: null, tourId: 'resources' },
      ],
    },
    {
      label: t?.nav?.sidebarAccount || 'ACCOUNT',
      items: [
        { label: t?.nav?.studioPlan || 'Studio Plan', href: '/pricing', icon: Crown, badge: null, tourId: undefined },
        { label: t?.nav?.notifications || 'Notifications', href: '#notifications', icon: Bell, badge: unreadCount > 0 ? String(unreadCount) : null, tourId: undefined, isNotif: true },
        { label: t?.nav?.profile || 'Profile', href: '/account', icon: User, badge: null, tourId: 'account' },
      ],
    },
  ];

  return (
    <aside
      className={`h-full flex flex-col transition-all duration-300 ease-in-out
        bg-card border-r border-border
        ${collapsed ? 'w-16' : 'w-64'}
      `}
    >
      {/* Brand */}
      <div
        className={`flex items-center border-b border-border shrink-0 ${
          collapsed ? 'py-5 justify-center px-2' : 'py-6 px-4'
        }`}
      >
        <Link
          href="/welcome"
          className={`flex items-center hover:opacity-90 transition-opacity cursor-pointer min-w-0 ${
            collapsed ? 'justify-center' : 'gap-4 w-full'
          }`}
        >
          <div
            className="relative inline-flex shrink-0 items-center justify-center overflow-visible"
            style={{ width: collapsed ? 62 : 88, height: collapsed ? 62 : 88 }}
          >
            <AmbientLogoGlow />
            <DreamSparkle />
            <AppLogo size={collapsed ? 62 : 88} />
          </div>
          {!collapsed && (
            <div className="flex flex-col justify-center min-w-0 flex-1 gap-2 pt-0.5">
              <span className="font-bold text-[13px] tracking-tight text-foreground leading-tight">
                AIMaker Studio
              </span>
              <span className="text-[8px] font-medium text-muted-foreground/70 tracking-[0.14em] uppercase leading-tight">
                Powered by DreamDNA™
              </span>
            </div>
          )}
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto scrollbar-hidden py-4 px-3">
        {navGroups.map((group, groupIndex) => (
          <div key={`group-${group.label}`} className={`mb-5 ${groupIndex > 0 ? 'mt-1' : ''}`}>
            {!collapsed && (
              <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/50">
                {group.label}
              </p>
            )}
            <div className="flex flex-col gap-1">
              {group.items.map((item, index) => {
                // Skip notification item — rendered separately below
                if ((item as any).isNotif) return null;

                const ItemIcon = item.icon;
                const active = pathname === item.href;
                const isHot = item.badge === 'Hot';
                const isNew = item.badge === 'New';
                const isBeta = item.badge === 'Beta';
                const isCount = item.badge && !isHot && !isNew && !isBeta;

                return (
                  <Link
                    key={`sidebar-${item.label}-${item.href}-${index}`}
                    href={item.href}
                    data-tour={item.tourId}
                    title={collapsed ? item.label : undefined}
                    className={`flex items-center gap-3 px-3 py-2 min-h-[40px] rounded-lg text-[13px] font-medium sidebar-item-hover
                      ${active ? 'sidebar-item-active' : 'text-muted-foreground'}
                      ${isHot && !active ? 'border border-primary/20 bg-primary/5' : ''}
                      ${collapsed ? 'justify-center px-2' : ''}
                    `}
                  >
                    <ItemIcon size={17} className={`shrink-0 sidebar-icon ${isHot && !active ? 'text-primary' : ''}`} />
                    {!collapsed && (
                      <>
                        <span className={`flex-1 truncate ${isHot && !active ? 'text-primary font-600' : ''}`}>{item.label}</span>
                        {item.badge && (
                          <span className={`text-[10px] font-600 px-1.5 py-0.5 rounded-full badge-fade-in badge-hover
                            ${isHot ? 'bg-accent/15 text-accent' : isNew ? 'bg-mint/15 text-mint' : isBeta ? 'bg-yellow-500/15 text-yellow-500' : 'bg-primary/15 text-primary'}
                          `}>
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Upgrade Banner — unpaid accounts (not labeled Free) */}
      {!collapsed && !isPaidActive && (
        <div className="mx-3 mb-4 p-3.5 rounded-xl glass-card border border-accent/20">
          <div className="flex items-center gap-2 mb-1.5">
            <Crown size={14} className="text-accent" />
            <span className="text-xs font-600 text-accent">{planLabel}</span>
          </div>
          <p className="text-[11px] text-muted-foreground mb-2 leading-relaxed">
            {t?.nav?.upgradeBannerText ||
              'Choose Creator or Studio to unlock your workspace.'}
          </p>
          <Link
            href="/pricing"
            className="block w-full text-center btn-accent px-3 py-1.5 rounded-lg text-xs"
          >
            {t?.nav?.upgradeNow || 'Choose a plan'}
          </Link>
        </div>
      )}

      {!collapsed && isPaidActive && (
        <div className="mx-3 mb-4 p-3.5 rounded-xl glass-card border border-primary/20">
          <div className="flex items-center gap-2">
            <Crown size={14} className="text-primary" />
            <span className="text-xs font-600 text-primary">{planLabel}</span>
            <span className="ml-auto text-[10px] font-700 px-1.5 py-0.5 rounded-full bg-mint/15 text-mint">{t?.nav?.sidebarActive || 'Active'}</span>
          </div>
        </div>
      )}

      {/* Bottom Actions */}
      <div className="border-t border-border p-3 flex flex-col gap-1">
        {!collapsed && (
          <div className="px-2 py-1 flex items-center gap-2">
            <LanguageSelector />
          </div>
        )}

        {/* Notifications button with panel */}
        <div className="relative">
          <button
            onClick={() => setNotifOpen(prev => !prev)}
            className={`flex items-center gap-3 px-3 py-2 min-h-[40px] rounded-lg text-[13px] font-medium text-muted-foreground sidebar-item-hover w-full ${collapsed ? 'justify-center px-2' : ''}`}
            title={collapsed ? (t?.nav?.notifications || 'Notifications') : undefined}
          >
            <div className="relative shrink-0">
              <Bell size={18} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-accent text-white text-[9px] font-700 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </div>
            {!collapsed && (
              <>
                <span className="flex-1 text-left">{t?.nav?.notifications || 'Notifications'}</span>
                {unreadCount > 0 && (
                  <span className="text-[10px] font-600 px-1.5 py-0.5 rounded-full bg-accent/15 text-accent">{unreadCount}</span>
                )}
              </>
            )}
          </button>

          {/* Notifications Panel */}
          {notifOpen && (
            <div
              className="absolute left-full bottom-0 ml-2 w-80 rounded-xl bg-card border border-border shadow-2xl overflow-hidden dropdown-enter"
              style={{ zIndex: 9999 }}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <div>
                  <p className="text-sm font-600 text-foreground">{t?.notifications?.title || 'Notifications'}</p>
                </div>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllRead}
                      className="text-[11px] text-primary hover:text-primary/80 transition-colors"
                    >
                      {t?.notifications?.markAllRead || 'Mark all read'}
                    </button>
                  )}
                  <button
                    onClick={() => setNotifOpen(false)}
                    className="p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>

              {/* Notification list */}
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                    <Bell size={28} className="text-muted-foreground/30 mb-3" />
                    <p className="text-sm font-500 text-foreground mb-1">{t?.notifications?.empty || 'No notifications yet'}</p>
                    <p className="text-xs text-muted-foreground">{t?.notifications?.emptyDesc || "We'll notify you when something happens — generations, credits, and more."}</p>
                  </div>
                ) : (
                  <div className="py-1">
                    {notifications.map((notif) => (
                      <div
                        key={notif.id}
                        className={`px-4 py-3 border-b border-border/50 last:border-0 transition-colors ${!notif.read ? 'bg-primary/3' : ''}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 shrink-0">{NOTIFICATION_ICONS[notif.type]}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-0.5">
                              <p className={`text-xs font-600 truncate ${!notif.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                                {notif.title}
                              </p>
                              {!notif.read && (
                                <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
                              )}
                            </div>
                            <p className="text-[11px] text-muted-foreground leading-relaxed">{notif.message}</p>
                            <p className="text-[10px] text-muted-foreground/60 mt-1">{notif.time}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-4 py-2.5 border-t border-border bg-muted/10">
                <p className="text-[10px] text-muted-foreground text-center">
                  {t?.notifications?.preferences || 'Notification preferences available in Account Settings'}
                </p>
              </div>
            </div>
          )}
        </div>

        <Link
          href="/profile"
          className={`flex items-center gap-3 px-3 py-2 min-h-[40px] rounded-lg text-[13px] font-medium text-muted-foreground sidebar-item-hover w-full ${collapsed ? 'justify-center px-2' : ''}`}
          title={collapsed ? (t?.nav?.profile || 'Profile') : undefined}
        >
          <User size={18} className="shrink-0" />
          {!collapsed && (
            <>
              <span className="flex-1 text-left">
                {identity?.fullName || t?.nav?.profile || 'Profile'}
              </span>
            </>
          )}
        </Link>
        <button
          onClick={handleSignOut}
          className={`flex items-center gap-3 px-3 py-2 min-h-[40px] rounded-lg text-[13px] font-medium text-muted-foreground sidebar-item-hover w-full ${collapsed ? 'justify-center px-2' : ''}`}
          title={collapsed ? (t?.nav?.signOut || 'Sign Out') : undefined}
        >
          <LogOut size={18} className="shrink-0" />
          {!collapsed && <span className="flex-1 text-left">{t?.nav?.signOut || 'Sign Out'}</span>}
        </button>

        <button
          onClick={() => onCollapse(!collapsed)}
          className={`flex items-center gap-3 px-3 py-2 min-h-[40px] rounded-lg text-[13px] font-medium text-muted-foreground sidebar-item-hover w-full ${collapsed ? 'justify-center px-2' : ''}`}
        >
          {collapsed ? <ChevronRight size={18} /> : (
            <>
              <ChevronLeft size={18} />
              <span>{t?.nav?.collapse || 'Collapse'}</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}